import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { ItemsApi } from '@/common/api/items';
import {
  invalidateItemsTreeSet,
  readTreeSetParents,
  treeSetReparented,
} from '@/common/queries/itemsTreeSetConsistency';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { bumpVisibleItemsCache } from '../context/VisibleItemsContext';

export interface RowMoveResult {
  failedCount: number;
  superseded?: boolean;
}

const requestReparent = async (
  itemIds: number[],
  newParentId: number | null,
  relativePriority: { id: number; type: 'before' | 'after' } | undefined,
): Promise<Set<number>> => {
  const fields = {
    parentId: newParentId,
    ...(relativePriority !== undefined && { relativePriority }),
  };
  try {
    const [itemId] = itemIds;
    if (itemIds.length === 1 && itemId !== undefined) {
      await ItemsApi.updateItem({ itemId, data: fields });
      return new Set();
    }
    const response = await ItemsApi.bulkPartialPatchItems({ itemIds, fields });
    return new Set(response.failed.map((failure) => failure.itemId));
  } catch {
    return new Set(itemIds);
  }
};

export const useItemReparent = (instanceKey: string) => {
  const queryClient = useQueryClient();

  return React.useCallback(
    async (
      itemIds: number[],
      newParentId: number | null,
      relativePriority?: { id: number; type: 'before' | 'after' },
    ): Promise<RowMoveResult> => {
      if (itemIds.length === 0) return { failedCount: 0 };
      const previousParentById = readTreeSetParents(queryClient, itemIds);
      const failedIds = await requestReparent(itemIds, newParentId, relativePriority);
      const movedIds = itemIds.filter((id) => !failedIds.has(id));
      if (movedIds.length === 0) return { failedCount: failedIds.size };

      // Await the refetch so the tree rebuilds before we bump the visible-items
      // cache. Both state updates land in the same React render batch, preventing
      // the skeleton flash that would otherwise appear while the grid waits for
      // updateRows to re-run.
      // Only the items the BE actually moved can show up under the new parent, so a
      // partial bulk failure must not keep us polling for the ones that were rejected.
      void invalidateItemsTreeSet(queryClient);
      await reconcileAndSettle(queryClient, movedIds, {
        isReflected: treeSetReparented(
          new Map([...previousParentById].filter(([id]) => !failedIds.has(id))),
          newParentId,
        ),
      });
      bumpVisibleItemsCache(queryClient, instanceKey);
      return { failedCount: failedIds.size };
    },
    [queryClient, instanceKey],
  );
};
